/**
 * Regras do envio de mídia — puras, testáveis sem navegador (PLANO 5.3).
 *
 * Ficam fora do componente porque são as decisões que doem se saírem erradas:
 * o tipo escolhido aqui determina por qual endpoint da Evolution o arquivo sai,
 * e o caminho montado aqui é o que as `storage.rules` autorizam. Errar
 * qualquer um dos dois só apareceria com o arquivo já no bucket.
 */

/**
 * Teto de 25 MB, o mesmo das `storage.rules`.
 *
 * As rules são a autoridade; esta constante existe para dar mensagem clara
 * ANTES de subir 30 MB e ouvir um `storage/unauthorized` sem explicação.
 * Manter os dois em dia é obrigação — ver o teste que amarra o número.
 */
export const LIMITE_BYTES = 25 * 1024 * 1024;

/** O que o seletor de arquivo oferece. */
export const TIPOS_ACEITOS = [
  'image/*',
  'video/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
].join(',');

/**
 * Tipo do domínio a partir do mimetype.
 *
 * São os mesmos quatro valores que a mídia RECEBIDA grava (`normalizar.js`), e
 * é o que faz o `MidiaMensagem` renderizar saída e entrada do mesmo jeito.
 * Qualquer coisa que não seja imagem/áudio/vídeo vira documento — o WhatsApp
 * aceita arquivo arbitrário como documento, então o padrão é o que funciona.
 */
export function tipoDoArquivo(mimetype) {
  const base = String(mimetype ?? '').split(';')[0].trim().toLowerCase();
  if (base.startsWith('image/')) return 'imagem';
  if (base.startsWith('audio/')) return 'audio';
  if (base.startsWith('video/')) return 'video';
  return 'documento';
}

/**
 * Nome seguro para virar segmento de caminho no Storage.
 *
 * O nome vem do computador do atendente: uma barra o mandaria para outra pasta,
 * e `..` para fora dela. Acento e espaço não quebram o Storage, mas atrapalham
 * na hora de baixar — viram hífen.
 */
export function nomeSeguro(bruto, reserva = 'arquivo') {
  const limpo = String(bruto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.-]+/, '')
    .slice(0, 100);
  return limpo || reserva;
}

/**
 * Caminho de SAÍDA no Storage.
 *
 * Pasta separada da mídia recebida (`.../midia/`) de propósito: é a pasta que
 * as `storage.rules` abrem para o navegador escrever. Deixar as duas juntas
 * significaria autorizar o navegador a sobrescrever o que o paciente mandou.
 */
export function caminhoDeSaida(clinicaId, conversaId, nome, agora = Date.now()) {
  const carimbo = `${agora}-${Math.random().toString(36).slice(2, 8)}`;
  return `clinicas/${clinicaId}/conversas/${conversaId}/saida/${carimbo}-${nomeSeguro(nome)}`;
}

const MB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** `null` quando o arquivo serve; a mensagem de recusa quando não. */
export function recusar(arquivo) {
  if (!arquivo) return 'Escolha um arquivo.';
  if (arquivo.size === 0) return 'Este arquivo está vazio.';
  if (arquivo.size > LIMITE_BYTES) {
    return `Arquivo de ${MB(arquivo.size)} — o limite é ${MB(LIMITE_BYTES)}.`;
  }
  return null;
}

/** "1,4 MB" para a prévia. */
export function tamanhoLegivel(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return MB(n).replace('.', ',');
}

/** `95` → `01:35`. O gravador precisa mostrar quanto já gravou. */
export function relogio(segundos) {
  const s = Math.max(0, Math.floor(Number(segundos) || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
